/**
 * Consent Management & Google Consent Mode v2
 * Full GDPR compliance with consent expiry, revocation, and cookie deletion
 */

export type ConsentCategories = {
  functional: boolean; // Always true (renamed from 'necessary' for consistency)
  analytics: boolean;
  marketing: boolean;
};

export const defaultConsent: ConsentCategories = {
  functional: true,
  analytics: false,
  marketing: false,
};

export const CONSENT_EXPIRY_DAYS = 365; // 1 year per GDPR recommendation

/**
 * Get saved consent from localStorage
 * Automatically checks expiry and clears if expired
 */
export function getSavedConsent(): ConsentCategories | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const saved = localStorage.getItem('user_consent');
    const timestamp = localStorage.getItem('consent_timestamp');
    
    if (!saved || !timestamp) return null;
    
    // Check expiry (1 year)
    const consentDate = new Date(timestamp);
    const now = new Date();
    const daysSince = (now.getTime() - consentDate.getTime()) / (1000 * 60 * 60 * 24);
    
    if (daysSince > CONSENT_EXPIRY_DAYS) {
      // Consent expired - clear and show banner again
      localStorage.removeItem('user_consent');
      localStorage.removeItem('consent_timestamp');
      localStorage.removeItem('consent_history');
      return null;
    }
    
    const parsed = JSON.parse(saved);
    return {
      functional: true, // Always true
      analytics: parsed.analytics || false,
      marketing: parsed.marketing || false,
    };
  } catch {
    return null;
  }
}

/**
 * Save consent to localStorage with timestamp and history
 */
export function saveConsent(consent: ConsentCategories): void {
  if (typeof window === 'undefined') return;
  
  const timestamp = new Date().toISOString();
  
  // Save current consent
  localStorage.setItem('user_consent', JSON.stringify(consent));
  localStorage.setItem('consent_timestamp', timestamp);
  
  // Save to history for audit trail
  try {
    const history = JSON.parse(localStorage.getItem('consent_history') || '[]');
    history.push({
      consent,
      timestamp,
      userAgent: navigator.userAgent,
    });
    // Keep last 10 entries only
    if (history.length > 10) history.shift();
    localStorage.setItem('consent_history', JSON.stringify(history));
  } catch (e) {
    // History is optional, don't fail if storage is full
  }
}

/**
 * Apply consent to Google Consent Mode v2
 */
export function applyConsentMode(consent: ConsentCategories): void {
  if (typeof window === 'undefined') return;
  
  const gtag = (window as any).gtag;
  if (!gtag) return;
  
  // Update Google Consent Mode v2
  gtag('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    functionality_storage: 'granted',
    personalization_storage: consent.analytics ? 'granted' : 'denied',
    security_storage: 'granted',
  });
  
  // Push consent update to GTM dataLayer for triggers
  const dataLayer = (window as any).dataLayer || [];
  dataLayer.push({
    'event': 'consent_update',
    'consent.analytics': consent.analytics ? 'granted' : 'denied',
    'consent.marketing': consent.marketing ? 'granted' : 'denied',
  });
}

/**
 * Initialize default consent mode (denied state)
 * This should run BEFORE any tracking scripts load
 */
export function initializeConsentMode(): void {
  if (typeof window === 'undefined') return;
  
  const gtag = (window as any).gtag;
  if (!gtag) return;
  
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });
}

/**
 * Delete a specific cookie
 * Works on both localhost and production domains
 * Uses multiple deletion strategies to ensure cookies are removed
 */
export function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  
  const hostname = window.location.hostname;
  
  // Strategy 1: Delete without domain (works for localhost)
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  
  // Strategy 2: Delete for localhost explicitly
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.localhost;`;
  }
  
  // Strategy 3: Delete for production domains (jamezz.com)
  if (hostname.includes('jamezz.com')) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.jamezz.com;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=www.jamezz.com;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=jamezz.com;`;
  }
  
  // Strategy 4: Delete for Azure domains (if applicable)
  if (hostname.includes('azurewebsites.net')) {
    const parts = hostname.split('.');
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname};`;
    if (parts.length > 2) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${parts.slice(-3).join('.')};`;
    }
  }
}

/**
 * Revoke specific consent type and delete associated cookies
 */
export function revokeConsent(type: 'analytics' | 'marketing' | 'all'): void {
  const consent = getSavedConsent() || defaultConsent;
  
  // Handle 'all' type
  if (type === 'all') {
    consent.analytics = false;
    consent.marketing = false;
  } else {
    consent[type] = false;
  }
  
  saveConsent(consent);
  applyConsentMode(consent);
  
  // Delete cookies based on revoked consent type
  if (type === 'analytics' || type === 'all') {
    // Google Analytics cookies
    deleteCookie('_ga');
    deleteCookie('_gid');
    deleteCookie('_gat');
    deleteCookie('_gat_gtag_' + process.env.NEXT_PUBLIC_GA4_ID);
    
    // Delete all _ga_* cookies (GA4 measurement IDs)
    if (typeof document !== 'undefined') {
      document.cookie.split(';').forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim();
        if (cookieName.startsWith('_ga_')) {
          deleteCookie(cookieName);
        }
      });
    }
    
    // HubSpot tracking cookie
    deleteCookie('hubspotutk');
    deleteCookie('__hstc');
    deleteCookie('__hssc');
    deleteCookie('__hssrc');
  }
  
  if (type === 'marketing' || type === 'all') {
    // Meta Pixel cookies
    deleteCookie('_fbp');
    deleteCookie('_fbc');
    deleteCookie('fr');
    
    // LinkedIn cookies
    deleteCookie('li_sugr');
    deleteCookie('bcookie');
    deleteCookie('lidc');
    deleteCookie('UserMatchHistory');
  }
  
  // Reload page to stop running scripts
  window.location.reload();
}

/**
 * Check if Do Not Track is enabled in browser
 */
export function isDNTEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  return navigator.doNotTrack === '1' || 
         (window as any).doNotTrack === '1' ||
         navigator.doNotTrack === 'yes' ||
         (navigator as any).msDoNotTrack === '1';
}

/**
 * Check if consent banner should be shown
 */
export function shouldShowConsentBanner(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Check if consent exists and is not expired
  const saved = getSavedConsent();
  return saved === null;
}

/**
 * Get consent snapshot for form submissions (GDPR audit trail)
 */
export function getConsentSnapshot(): string {
  const consent = getSavedConsent() || defaultConsent;
  const timestamp = localStorage.getItem('consent_timestamp') || new Date().toISOString();
  
  return JSON.stringify({
    functional: consent.functional,
    analytics: consent.analytics,
    marketing: consent.marketing,
    timestamp,
    dnt: isDNTEnabled(),
  });
}

/**
 * Reset all consent (for testing or user request)
 */
/**
 * Reset consent settings (used by Cookie Settings button in footer)
 * Removes localStorage AND deletes all tracking cookies
 */
export function resetConsent(): void {
  if (typeof window === 'undefined') return;
  
  // Remove localStorage items
  localStorage.removeItem('user_consent');
  localStorage.removeItem('consent_timestamp');
  
  // Don't clear history - keep for audit
  // localStorage.removeItem('consent_history');
  
  // Delete ALL tracking cookies (analytics + marketing)
  // Google Analytics/GTM cookies
  deleteCookie('_ga');
  deleteCookie('_gid');
  deleteCookie('_ga_Q4BCR3MD0E'); // Our GA4 measurement ID
  deleteCookie('_gat');
  deleteCookie('_gac_gb_Q4BCR3MD0E');
  
  // HubSpot cookies
  deleteCookie('__hstc');
  deleteCookie('hubspotutk');
  deleteCookie('__hssrc');
  deleteCookie('__hssc');
  
  // Meta Pixel cookies
  deleteCookie('_fbp');
  deleteCookie('_fbc');
  deleteCookie('fr');
  
  // LinkedIn cookies
  deleteCookie('li_sugr');
  deleteCookie('bcookie');
  deleteCookie('lidc');
  deleteCookie('UserMatchHistory');
}
